import CheckmarkOutline16 from '@carbon/icons-react/es/checkmark--outline/16';
import Close16 from '@carbon/icons-react/es/close/16';
import Close20 from '@carbon/icons-react/es/close/20';
import Draggable16 from '@carbon/icons-react/es/draggable/16';
import Edit16 from '@carbon/icons-react/es/edit/16';
import { DraggableSyntheticListeners, useDroppable } from '@dnd-kit/core';
import { useBoolean } from 'ahooks';
import { Space, Tooltip, Typography } from 'antd';
import React, { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import { ifProp, prop } from 'styled-tools';
import { FieldSectionType } from 'api/field-sections/types';
import { Field } from 'api/tasks';
import { Button } from 'components/atoms/button';
import { Input } from 'components/atoms/input';
import { OutsideClickHandler } from 'components/atoms/outside-click-handler';
import { Popconfirm } from 'components/atoms/popconfirm';
import { Switch } from 'components/atoms/switch/switch';
import { toastService } from 'components/molecules/toast';
import { canMoveField } from 'components/organisms/module-settings/fields';
import { Text02, Text03 } from 'components/styles';
import { useTranslation } from 'hooks/use-translation';
import {
  useCreatingFieldSectionMutationV2 as useCreatingFieldSectionMutation,
  useDeleteFieldSectionMutationV2 as useDeleteFieldSectionMutation,
  useUpdateFieldSectionMutationV2 as useUpdateFieldSectionMutation,
} from 'queries/field-sections';
import {
  FieldStatus,
  FieldType,
  MoreOption,
} from '../module-settings/fields/task/list-item-components';
import { FieldSectionMapping } from './section-list';

const { Text } = Typography;

const GridWrapper = styled.div`
  display: grid;
  grid-template-columns: var(--spacing-06) repeat(4, 1fr) var(--spacing-09);
`;

const ConfirmButton = styled(Button).attrs({
  icon: <CheckmarkOutline16 />,
  shape: 'round',
  size: 'small',
  type: 'link',
})`
  color: var(--green-50);
`;

const CancelEditButton = styled(Button).attrs({
  icon: <Close16 />,
  shape: 'round',
  size: 'small',
  type: 'link',
})`
  color: var(--error-color);
`;

const EditButton = styled(Edit16)`
  cursor: pointer;
  display: none;
`;
const DeleteSectionButtonWrapper = styled.div`
  margin-left: auto;
`;
const DeleteSectionButton = styled(Close20)`
  color: var(--error-color);
  cursor: pointer;
  display: none;
  &.ant-popover-open {
    display: block;
  }
`;

const DisabledText = styled.span`
  color: var(--gray-50);
`;

const SectionHeadingWrapper = styled.div<{
  isPrivateSection: boolean;
  isTaskType: boolean;
}>`
  background-color: ${({ isPrivateSection, isTaskType }) =>
    isPrivateSection && isTaskType
      ? 'var(--private-01)'
      : 'var(--coolest-gray-10)'};
  height: 48px;
  padding: var(--spacing-05) var(--spacing-04) var(--spacing-05)
    var(--spacing-07);
  display: flex;
  align-items: center;
  &:hover {
    ${EditButton},
    ${DeleteSectionButton} {
      display: block;
    }
    background-color: ${({ isPrivateSection, isTaskType }) =>
      isPrivateSection && isTaskType
        ? 'var(--private-02)'
        : 'var(--coolest-gray-10)'};
  }
`;

const SectionName = styled.div`
  margin-right: var(--spacing-04);
`;
const SectionNameInput = styled(Input)`
  width: 200px;
  margin-right: var(--spacing-04);
`;

const SpaceStyled = styled(Space)`
  padding: 0 var(--spacing-06);
  position: relative;
  bottom: var(--spacing-01);
`;

export const SectionNameHeader = ({
  fieldSections,
  sectionId,
}: {
  fieldSections: Record<number, FieldSectionMapping>;
  sectionId: number;
}): JSX.Element => {
  const fieldSection = fieldSections[sectionId];
  const { fieldSectionType } = fieldSection;

  const defaultSectionName = useMemo(() => {
    let name = '';

    Object.keys(fieldSections).forEach((key) => {
      if (fieldSections[key].default) {
        const defaultFieldSection: FieldSectionMapping = fieldSections[key];

        name = defaultFieldSection.fieldSectionName;
      }
    });

    return name;
  }, [fieldSections]);

  const [
    isEditing,
    { setTrue: setIsEditingTrue, setFalse: setIsEditingFalse },
  ] = useBoolean(false);
  const [updatedSectionName, setUpdatedSectionName] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const { mutate: updateFieldSection, isLoading } =
    useUpdateFieldSectionMutation(fieldSectionType);
  const { mutate: deleteFieldSection } =
    useDeleteFieldSectionMutation(fieldSectionType);

  const sectionEditSuccessTitle = useTranslation(
    'settings.fields.section.edit.success.title',
  );
  const sectionEditSuccessMessage = useTranslation(
    'settings.fields.section.edit.success.message',
    { fieldSectionName: updatedSectionName },
  );
  const errorTitle = useTranslation('failure');
  const sectionEditErrorTitle = useTranslation(
    'settings.fields.section.edit.error.title',
  );
  const sectionDeleteErrorTitle = useTranslation(
    'settings.fields.section.delete.error.title',
  );
  const handleConfirm = () => {
    if (!updatedSectionName) {
      return;
    }
    updateFieldSection(
      {
        fieldSectionId: fieldSection.fieldSectionId,
        fieldSectionName: updatedSectionName,
        fieldSectionType,
      },
      {
        onSuccess: () => {
          setIsEditingFalse();
          toastService({
            title: sectionEditSuccessTitle,
            message: sectionEditSuccessMessage,
          }).success();
        },
        onError: () => {
          toastService({
            title: errorTitle,
            message: sectionEditErrorTitle,
          }).error();
        },
      },
    );
  };

  const isDeletable =
    !fieldSection.default &&
    !(
      fieldSection.fieldSectionType === FieldSectionType.Task &&
      fieldSection.fixed
    );

  const handleEditButtonClick = () => {
    setIsEditingTrue();
    setUpdatedSectionName(fieldSection.fieldSectionName);
    setIsPrivate(fieldSection.private ?? false);
  };

  const handleCancel = () => {
    setIsEditingFalse();
  };

  const isUserSection =
    fieldSection.fieldSectionType === FieldSectionType.Users;

  const isNonemptyUserSection =
    isUserSection && fieldSection.fieldOrder.length > 0;

  return (
    <SectionHeadingWrapper
      isPrivateSection={fieldSection.private ?? false}
      isTaskType={fieldSection.fieldSectionType === FieldSectionType.Task}
    >
      {isEditing ? (
        <>
          <OutsideClickHandler onOutsideClick={handleConfirm}>
            <SectionNameInput
              size="small"
              value={updatedSectionName}
              onChange={(e) => setUpdatedSectionName(e.target.value)}
              autoFocus
            />
            {fieldSectionType === 'TASK' && (
              <SpaceStyled align="center">
                <Switch checked={isPrivate} disabled size="small" />

                <DisabledText>
                  <FormattedMessage id="settings.fields.section.task.create.private" />
                </DisabledText>
              </SpaceStyled>
            )}
            <ConfirmButton
              onClick={handleConfirm}
              disabled={isLoading || !updatedSectionName}
            />
            <CancelEditButton onClick={handleCancel} disabled={isLoading} />
          </OutsideClickHandler>
        </>
      ) : (
        <>
          <SectionName>{fieldSection.fieldSectionName}</SectionName>
          <EditButton onClick={handleEditButtonClick} />
        </>
      )}
      {isDeletable && !isEditing && (
        <DeleteSectionButtonWrapper>
          {isNonemptyUserSection ? (
            <Tooltip
              placement="top"
              title={
                <FormattedMessage
                  id="settings.fields.team_member_fields.non_empty_section.delete.message"
                  values={{
                    sectionName: fieldSection.fieldSectionName,
                    defaultSectionName,
                  }}
                />
              }
            >
              <DeleteSectionButton />
            </Tooltip>
          ) : (
            <Popconfirm
              placement="leftTop"
              subTitle={
                <FormattedMessage
                  id={
                    isUserSection
                      ? 'settings.fields.team_member_fields.empty_section.delete.message'
                      : 'settings.fields.section.delete.message'
                  }
                  values={{
                    sectionName: fieldSection.fieldSectionName,
                    defaultSectionName,
                  }}
                />
              }
              onConfirm={() => {
                deleteFieldSection(fieldSection.fieldSectionId, {
                  onError: () => {
                    toastService({
                      title: errorTitle,
                      message: sectionDeleteErrorTitle,
                    }).error();
                  },
                });
              }}
            >
              <DeleteSectionButton />
            </Popconfirm>
          )}
        </DeleteSectionButtonWrapper>
      )}
    </SectionHeadingWrapper>
  );
};

export const NewSectionCreation = ({
  defaultSectionName,
  fieldSectionType,
  onCancel,
  onSectionCreationSuccess,
}: {
  defaultSectionName: string;
  fieldSectionType: FieldSectionType;
  onCancel: () => void;
  onSectionCreationSuccess: () => void;
}): JSX.Element => {
  const [updatedSectionName, setUpdatedSectionName] =
    useState(defaultSectionName);
  const [isPrivate, setIsPrivate] = useState(false);

  const { mutate, isLoading } =
    useCreatingFieldSectionMutation(fieldSectionType);

  const sectionCreationSuccessTitle = useTranslation(
    'settings.fields.section.create.success.title',
  );
  const sectionCreationSuccessMessage = useTranslation(
    'settings.fields.section.create.success.message',
    { fieldSectionName: updatedSectionName },
  );
  const errorTitle = useTranslation('failure');
  const sectionCreationErrorTitle = useTranslation(
    'settings.fields.section.create.error.title',
  );
  const handleConfirm = () => {
    if (!updatedSectionName) {
      return;
    }
    mutate(
      {
        fieldSectionName: updatedSectionName,
        fieldSectionType,
        private: isPrivate,
      },
      {
        onSuccess: () => {
          onSectionCreationSuccess();
          toastService({
            title: sectionCreationSuccessTitle,
            message: sectionCreationSuccessMessage,
          }).success();
        },
        onError: () => {
          toastService({
            title: errorTitle,
            message: sectionCreationErrorTitle,
          }).error();
        },
      },
    );
  };

  return (
    <SectionHeadingWrapper
      isPrivateSection={isPrivate}
      isTaskType={fieldSectionType === FieldSectionType.Task}
    >
      <OutsideClickHandler onOutsideClick={handleConfirm}>
        <SectionNameInput
          size="small"
          value={updatedSectionName}
          onChange={(e) => setUpdatedSectionName(e.target.value)}
          autoFocus
        />
        {fieldSectionType === 'TASK' && (
          <SpaceStyled align="center">
            <Switch
              checked={isPrivate}
              onChange={() => setIsPrivate(!isPrivate)}
              size="small"
            />

            <span>
              <FormattedMessage id="settings.fields.section.task.create.private" />
            </span>
          </SpaceStyled>
        )}
        <ConfirmButton
          onClick={handleConfirm}
          disabled={isLoading || !updatedSectionName}
        />
        <CancelEditButton onClick={onCancel} disabled={isLoading} />
      </OutsideClickHandler>
    </SectionHeadingWrapper>
  );
};

const SectionTable = styled.div`
  margin-bottom: var(--spacing-08);
`;

const FieldHeader = styled.div`
  background: #edeef0;
  font-weight: var(--heading-6-weight);
  padding: var(--spacing-03);
`;

export const FieldMappingContainer = ({
  children,
  fieldSections,
  sectionId,
}: {
  children: React.ReactNode;
  fieldSections: Record<number, FieldSectionMapping>;
  sectionId: number;
}): JSX.Element => {
  const fieldSection = fieldSections[sectionId];
  const { setNodeRef } = useDroppable({
    id: fieldSection.fieldSectionId.toString(),
  });

  return (
    <SectionTable ref={setNodeRef}>
      <SectionNameHeader fieldSections={fieldSections} sectionId={sectionId} />
      <GridWrapper>
        <FieldHeader />
        <FieldHeader>
          <FormattedMessage id="settings.fields.field.name" />
        </FieldHeader>
        <FieldHeader>
          <FormattedMessage id="description" />
        </FieldHeader>
        <FieldHeader>
          <FormattedMessage id="settings.fields.field.type" />
        </FieldHeader>
        <FieldHeader>
          <FormattedMessage id="status" />
        </FieldHeader>
        <FieldHeader />
      </GridWrapper>
      {children}
    </SectionTable>
  );
};

const DraggableHandle = styled.div`
  cursor: grab;
  padding-top: var(--spacing-02);
`;

const FieldRowItem = styled.div<{ isPrivate?: boolean }>`
  display: grid;
  grid-template-columns: var(--spacing-06) repeat(4, 1fr) var(--spacing-09);
  border-bottom: 1px solid var(--border-color);
  height: var(--height-lg);
  &:hover {
    background-color: var(--table-row-hover);
  }
  ${ifProp(
    prop('isPrivate', false),
    css`
      background-color: var(--private-01);
      &:hover {
        background-color: var(--private-02) !important;
      }
    `,
  )};
`;

const FieldItem = styled.div`
  padding: 0 var(--spacing-03);
  display: flex;
  align-items: center;
`;

const FieldDescription = styled.div`
  &,
  & svg {
    ${Text02};
  }
`;

interface FieldRowProps {
  field: Field;
  draggableListeners?: DraggableSyntheticListeners;
}
export const FieldRow = ({
  field,
  draggableListeners,
}: FieldRowProps): JSX.Element => {
  const showReorder = canMoveField(field);

  return (
    <FieldRowItem
      isPrivate={field.private}
      data-cy="div.field-row"
      data-test={field.fieldLabel}
      data-field-id={field.fieldId}
    >
      <FieldItem>
        {showReorder ? (
          <DraggableHandle {...draggableListeners}>
            <Draggable16 />
          </DraggableHandle>
        ) : (
          <></>
        )}
      </FieldItem>
      <FieldItem>
        <Text ellipsis={{ tooltip: true }}>{field.fieldLabel}</Text>
      </FieldItem>
      <FieldItem>
        <FieldDescription>
          <Text ellipsis={{ tooltip: true }}>{field.description}</Text>
        </FieldDescription>
      </FieldItem>
      <FieldItem>
        <FieldType field={field} />
      </FieldItem>
      <FieldItem>
        <FieldStatus field={field} value={field.isEnabled} />
      </FieldItem>
      <FieldItem>
        <MoreOption field={field} />
      </FieldItem>
    </FieldRowItem>
  );
};

const EmptyState = styled.div`
  padding: var(--spacing-02) var(--spacing-03);
  font-size: var(--font-size-sm);
  ${Text03};
`;

export const SectionTableEmptyState = () => {
  return (
    <EmptyState>
      <FormattedMessage id="settings.fields.section.empty.message" />
    </EmptyState>
  );
};
